import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  Card,
  CardContent,
  Typography as MuiTypography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setEmail, setPhone } from "../../../store/setProfile";

export default function ContactInfo({ onDirtyChange, onValidChange, onSave }: any) {
  const dispatch = useDispatch();
  const { email, phone } = useSelector(
    (state: any) => state.profile.information
  );
  const [mailError, setMailError] = useState(false);

  useEffect(() => {
    onDirtyChange?.(false);
  }, []);

  const validate = (name: string, value: string) => {
    let isValid = false;
    switch (name) {
      case "email":
        isValid = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i.test(value);
        break;
      case "phone":
        isValid = !value || /^(?:(?:\+|00)44|0)7(?:[1345789]\d{2}|624)\d{6}$/i.test(value);
        break;
      default:
        break;
    }
    return isValid;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const isValid = validate("email", value);
      setMailError(!isValid);
      dispatch(setEmail(value));
    } else {
      setMailError(false);
      dispatch(setEmail(value));
    }
    onDirtyChange?.(true);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setPhone(value));
    onDirtyChange?.(true);
  };

  useEffect(() => {
    // Optional step, always valid
    onValidChange?.(true);
  }, [onValidChange]);

  (ContactInfo as any).save = async () => {
    onDirtyChange?.(false);
  };

  return (
    <Box>
      <Typography 
        variant="h2"
        sx={{ 
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
        How do clients get in touch with you?
        </Box>
      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          (This section is optional)
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <MuiTypography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "#6b7280",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            You will receive emails from potential clients via our message system
            and will be notified by email; the one you use to sign up with. In
            addition to this, you can have your email / telephone number show on
            your profile for others to see. Of course, this is optional!
          </MuiTypography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="email-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              Your Email{" "}
              <MuiTypography
                component="span"
                sx={{ fontSize: "0.875rem", color: "grey", fontWeight: 400 }}
              >
                (Optional)
              </MuiTypography>
            </FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter your email"
              value={email || ""}
              onChange={handleEmailChange}
              error={mailError}
              helperText={mailError ? "Please enter a valid email address" : ""}
            />
            <MuiTypography
              variant="caption"
              sx={{ mt: 0.5, color: "grey", fontSize: "0.875rem" }}
            >
              Your email will be shown in your profile
            </MuiTypography>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel
              id="phone-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              Your Telephone{" "}
              <MuiTypography
                component="span"
                sx={{ fontSize: "0.875rem", color: "grey", fontWeight: 400 }}
              >
                (Optional)
              </MuiTypography>
            </FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter your phone"
              type="number"
              value={phone || ""}
              onChange={handlePhoneChange}
              onKeyDown={(e) => {
                if (e.key === "e" || e.key === ".") {
                  e.preventDefault();
                }
              }}
            />
            <MuiTypography
              variant="caption"
              sx={{ mt: 0.5, color: "grey", fontSize: "0.875rem" }}
            >
              Your telephone number will be shown on your profile
            </MuiTypography>
          </FormControl>
        </CardContent>
      </Card>
    </Box>
  );
}
